import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { useQuery } from '@tanstack/react-query'
import {
  AccountFitSettings,
  COUNTRY_SELECT_OPTIONS,
  EMPLOYEE_COUNTS_BY_RANGE_NAME,
  INDUSTRY_SELECT_OPTIONS
} from 'constants/firmographics'

import { SelectOption } from '@driftt/dds'

import { fetchAccountFitFirmographics } from 'api/AccountScoringAPI'
import { getCurrentOrg } from 'selectors'
import {
  accountFitScoringCompletedAtLeastOnce,
  AccountFitSetupStatus,
  fetchAccountFitSetupStatus,
  isAccountFitEnabled} from 'utils/features'

export const companySizeOptions = Object.keys(EMPLOYEE_COUNTS_BY_RANGE_NAME).map(range => ({
  label: `${EMPLOYEE_COUNTS_BY_RANGE_NAME[range]} (${range})`,
  value: range
}))

const useICPSettings = () => {
  const org = useSelector(getCurrentOrg)

  const [showPreferencesModal, setShowPreferencesModal] = useState(false)

  const [accountFitEnabled, setAccountFitEnabled] = useState<boolean>(isAccountFitEnabled(org))
  const [accountFitSetup, setAccountFitSetup] = useState<AccountFitSetupStatus>(fetchAccountFitSetupStatus(org))
  const [scoringCompletedAtLeastOnce, setScoringCompletedAtLeastOnce] = useState<boolean>(
    accountFitScoringCompletedAtLeastOnce(org)
  )

  useEffect(() => {
    setAccountFitEnabled(org?.properties?.accountFitEnabled)
  }, [org?.properties?.accountFitEnabled])

  useEffect(() => {
    setAccountFitSetup(fetchAccountFitSetupStatus(org))
  }, [org])

  useEffect(() => {
    setScoringCompletedAtLeastOnce(org?.properties?.accountFitScoringCompletedAtLeastOnce)
  }, [org?.properties?.accountFitScoringCompletedAtLeastOnce])

  const [defaultData, setDefaultData] = useState<AccountFitSettings>({
    EmployeeCount: [],
    Countries: [],
    Industries: []
  })

  const ICP_SWR_KEY = 'ACCOUNT_FIT_FIRMOGRAPHICS'
  const { error } = useQuery<AccountFitSettings>({
    queryKey: [ICP_SWR_KEY],
    queryFn: async () => {
      const data = await fetchAccountFitFirmographics()
      applyDataToSelects(data)
      setDefaultData(data)
      return data
    }
  })

  const applyDataToSelects = useCallback((firmoData: AccountFitSettings) => {
    const companySizeSelected = getOptionsFromValues(firmoData['EmployeeCount'], companySizeOptions)
    const countriesSelected = getOptionsFromValues(
      firmoData['Countries'],
      COUNTRY_SELECT_OPTIONS as SelectOption<string>[]
    )
    const industriesSelected = getOptionsFromValues(
      firmoData['Industries'],
      INDUSTRY_SELECT_OPTIONS as SelectOption<string>[]
    )
    setCompanySizes(companySizeSelected)
    setCountries(countriesSelected)
    setIndustries(industriesSelected)
  }, [])

  const [companySizes, setCompanySizes] = useState<SelectOption<string>[]>([])
  const [countries, setCountries] = useState<SelectOption<string>[]>([])
  const [industries, setIndustries] = useState<SelectOption<string>[]>([])

  const hasFirmoSettings = useMemo(() => {
    const localSettings = JSON.parse(
      localStorage.getItem('firmographic_settings_temp') ?? '{}'
    ) as AccountFitSettings
    return (
      Object.values(localSettings).some(values => values.length > 0) ||
      Object.values(defaultData).some(values => values.length > 0)
    )
  }, [defaultData])

  const noParameters = useMemo(
    () => accountFitEnabled && hasFirmoSettings === false && scoringCompletedAtLeastOnce,
    [accountFitEnabled, hasFirmoSettings, scoringCompletedAtLeastOnce]
  )

  const scoringInProgress = useMemo(() => {
    return (
      (accountFitEnabled && accountFitSetup === AccountFitSetupStatus.NOT_COMPLETE && scoringCompletedAtLeastOnce) ||
      (accountFitEnabled && accountFitSetup === AccountFitSetupStatus.NOT_COMPLETE && hasFirmoSettings)
    )
  }, [accountFitEnabled, accountFitSetup, hasFirmoSettings, scoringCompletedAtLeastOnce])

  const hideScoresBehindLock = useMemo(
    () => accountFitEnabled && accountFitSetup === AccountFitSetupStatus.NOT_COMPLETE && hasFirmoSettings === false,
    [accountFitEnabled, accountFitSetup, hasFirmoSettings]
  )

  const scoringWorkflowError = useMemo(() => {
    return accountFitEnabled && accountFitSetup === AccountFitSetupStatus.ERROR
  }, 
  [accountFitEnabled, accountFitSetup])

  const getOptionsFromValues = (data: string[], options: SelectOption<string>[]) => {
    return options.filter(opt => data.includes(opt.value))
  }

  return {
    accountFitEnabled,
    setAccountFitEnabled,
    accountFitSetup,
    setAccountFitSetup,
    scoringCompletedAtLeastOnce,
    hasFirmoSettings,
    noParameters,
    scoringInProgress,
    companySizes,
    setCompanySizes,
    countries,
    setCountries,
    industries,
    setIndustries,
    ICP_SWR_KEY,
    applyDataToSelects,
    error,
    showPreferencesModal,
    setShowPreferencesModal,
    hideScoresBehindLock,
    defaultData,
    scoringWorkflowError
  }
}

export default useICPSettings
